import { useState } from 'react';
import { LoadingIndicator } from '@m/alchemy-ui/LoadingIndicator';
import { ConfirmIcon } from '@m/alchemy-ui/icons/ConfirmIcon';
import { useDropzone } from 'react-dropzone';
import styled, { css } from 'styled-components';
import { uploadImage } from '../../../../../utils/usersApi';
import { validationMessages } from './validationMessages';
import { useController } from 'react-hook-form';
import { StyledField } from '../styles';
import { Button } from '@m/alchemy-ui/Button';
import { color, snap } from '@m/alchemy-theme';

const UserProfileContainerWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding-left: ${snap(50)};
`;
const Container = styled.div<{
  isDragAccept: boolean;
  isDragReject: boolean;
  isDragActive: boolean;
}>`
  align-items: center;
  padding: 0;
  cursor: pointer;
  border-width: 2px;
  border-radius: 2px;
  border-style: dashed;
  background: ${color('canvas')};
  outline: none;
  transition: border 0.24s ease-in-out;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  position: relative;
  overflow: hidden;
  border-color: transparent;

  margin-bottom: 10px;

  ${props =>
    props.isDragAccept &&
    css`
      border-color: ${color('base', { palette: 'structural' })};
    `}
  ${props =>
    props.isDragReject &&
    css`
      border-color: ${color('base', { palette: 'structural' })};
    `}
  ${props =>
    props.isDragActive &&
    css`
      border-color: ${color('base', { palette: 'structural' })};
    `}
`;
const ThumbContainer = styled.div`
  width: 100%;
`;
const ThumbImage = styled.img`
  max-width: 100%;
  height: auto;
`;

const IconsContainer = styled.div`
  width: ${snap(50)};
  padding: 0 10px;
`;
const PreviewImg = styled.img`
  position: absolute;
  left: 50%;
  top: 50%;
  padding: 0;
  transform: translate(-50%, -50%);
  border-radius: 50%;
`;

type FileWithPreview = File & {
  preview: string;
};

interface PreviewUploadedImageProps {
  defaultValue?: string;
  files: FileWithPreview[];
}

const PreviewUploadedImage = ({ defaultValue = '', files = [] }: PreviewUploadedImageProps) => {
  if (files.length) {
    return files.map(file => (
      <ThumbContainer key={file.name}>
        <ThumbImage alt={file.name} src={file.preview} loading="lazy" />
      </ThumbContainer>
    ));
  }
  if (!defaultValue) {
    return null;
  }
  return <PreviewImg src={defaultValue} alt="Avatar" width="120" height="120" />;
};

interface UploadImageProps {
  buttonText: string;
  label?: string;
  required?: boolean;
  defaultValue?: string;
  name: string;
  control: any;
  validate?: any;
}

export const UploadImage = ({
  buttonText,
  label = '',
  required = false,
  name,
  control,
  validate = {},
}: UploadImageProps) => {
  const options = {
    ...(required && { required: validationMessages.required }),
    ...(validate && { validate }),
  };

  const { field, fieldState } = useController({
    name,
    control,
    rules: options,
  });

  const { invalid, error } = fieldState;

  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject, open } =
    useDropzone({
      accept: 'image/*',
      maxFiles: 1,
      multiple: false,
      onDrop: acceptedFiles =>
        setFiles(
          acceptedFiles.map(file =>
            Object.assign(file, {
              preview: URL.createObjectURL(file),
            })
          )
        ),
    });

  const uploadAndUpdate = async file => {
    const data = await uploadImage(file);
    setIsImageUploading(false);
    field.onChange(data.data.secure_url);
  };

  const handleChange = e => {
    setIsImageUploading(true);
    uploadAndUpdate(e.target.files[0]);
  };

  return (
    <StyledField
      required={required}
      labelVariant="emphasized"
      label={label}
      status={invalid ? { level: 'error', message: error?.message } : undefined}
      style={{ display: 'block' }}
      input={
        <UserProfileContainerWrapper>
          <div className="text-center">
            <Container {...getRootProps({ isDragActive, isDragAccept, isDragReject })}>
              <input {...getInputProps({ onChange: handleChange })} />
              <PreviewUploadedImage defaultValue={field.value} files={files} />
            </Container>
            <Button priority="secondary" onClick={open} size="small">
              {buttonText}
            </Button>
          </div>
          <IconsContainer>
            {isImageUploading && <LoadingIndicator inline />}
            {!isImageUploading && field.value && (
              <ConfirmIcon aria-label="Confirm" color="lineContrast" />
            )}
          </IconsContainer>
        </UserProfileContainerWrapper>
      }
    />
  );
};
